<template>
  <RevModalBase
    class="bg-surface-default-low left-1/2 top-0 mx-auto max-h-none -translate-x-1/2 lg:rounded-t-lg lg:top-24 lg:max-w-[1168px]"
    :has-body-overflow="false"
    :has-padding="false"
    name="COMPARISON_MODAL"
    :transition-class-names
    @close="handleClose"
    @open="handleOpen"
  >
    <template #beforeTitle>
      <div class="hidden items-center justify-between pt-8 lg:flex">
        <div class="flex items-center space-x-16">
          <div
            class="bg-static-info-mid rounded-lg flex h-48 w-48 items-center justify-center"
          >
            <IconCompare />
          </div>
          <div>
            <div class="heading-3">
              {{ i18n(translations.compareDevices) }}
            </div>
            <div class="text-static-default-low body-2">
              {{ i18n(translations.selectProducts) }}
            </div>
          </div>
        </div>
      </div>
      <span
        class="text-static-default-hi body-1 absolute left-1/2 mt-8 w-full -translate-x-1/2 px-40 text-center lg:hidden"
        >{{ i18n(translations.modalTitle) }}</span
      >
    </template>
    <template #body>
      <div class="flex h-full flex-col px-24 pt-32 lg:pt-24">
        <div
          class="border-static-default-low relative min-h-0 overflow-hidden border-t lg:border-none"
        >
          <div class="h-full overflow-auto pb-48 pt-24 lg:pt-0">
            <Transition
              enter-active-class="ease-out duration-150"
              enter-from-class="-translate-y-full"
              enter-to-class="translate-y-0"
              leave-active-class="ease-in duration-150"
              leave-from-class="translate-y-0"
              leave-to-class="-translate-y-full"
            >
              <div
                v-show="isScrolled"
                class="bg-surface-default-low border-static-default-low absolute left-0 right-0 top-0 z-[1] border-b pb-12 pt-16 lg:pb-24 lg:pt-0"
                :class="gridClasses"
              >
                <ProductComparisonModalCard
                  v-for="product in products"
                  :key="`sticky-card-${product.listingId}`"
                  :product
                  sticky
                />
              </div>
            </Transition>
            <div class="space-y-48 lg:space-y-64">
              <div ref="bodyContainer" :class="gridClasses">
                <ProductComparisonModalCard
                  v-for="product in products"
                  :key="`card-${product.listingId}`"
                  :product
                />
              </div>
              <div v-if="productsSpecificationsLoading">
                <div :class="gridClasses">
                  <div
                    v-for="product in products"
                    :key="`spinner-${product.listingId}`"
                    class="flex justify-center"
                  >
                    <RevSpinner
                      :alternative-text="i18n(translations.loading)"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="space-y-48 lg:space-y-64">
                <div
                  v-for="(
                    { title, sections, separator }, tableGroupIndex
                  ) in tableData"
                  :key="`table-group-${tableGroupIndex}`"
                  :class="{
                    'border-static-default-low border-t pt-48 lg:pt-64':
                      separator,
                  }"
                >
                  <p class="punchline mb-12">{{ title }}</p>
                  <div
                    class="divide-static-default-low space-y-48 divide-y lg:space-y-64"
                  >
                    <div
                      v-for="(
                        { title: sectionTitle, trackingKey, lines },
                        sectionIndex
                      ) in sections"
                      :id="trackingKey"
                      :key="`${tableGroupIndex}_${sectionIndex}`"
                      ref="sectionRef"
                      class="pt-48 first:pt-0 lg:pt-64"
                    >
                      <p v-if="sectionTitle" class="heading-2 mb-8">
                        {{ sectionTitle }}
                      </p>
                      <div class="space-y-20">
                        <div
                          v-for="(line, lineIndex) in lines"
                          :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}`"
                        >
                          <div v-if="line.type === 'scores'">
                            <div :class="gridClasses">
                              <div
                                v-for="(score, scoreIndex) in line.values"
                                :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_${scoreIndex}`"
                              >
                                <ProductScore
                                  v-if="score"
                                  :label="sectionTitle || ''"
                                  :value="score"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="line.type === 'info'"
                            class="bg-static-info-mid rounded-sm hover:bg-static-info-mid-hover"
                          >
                            <RevAccordionItem
                              class="!p-16 hover:bg-transparent"
                            >
                              <template #title>
                                <div class="flex items-center">
                                  <IconLightbulb class="mr-8" />
                                  <span class="body-2-bold">{{
                                    i18n(translations.importanceTitle)
                                  }}</span>
                                </div>
                              </template>
                              <template #body>
                                <p class="-mt-8 pl-24">{{ line.value }}</p>
                              </template>
                            </RevAccordionItem>
                          </div>
                          <div v-else>
                            <p v-if="line.title" class="body-1-bold mb-8">
                              {{ line.title }}
                            </p>
                            <div
                              class="bg-surface-default-mid rounded-lg"
                              :class="gridClasses"
                            >
                              <div
                                v-for="(value, valueIndex) in line.values"
                                :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_${valueIndex}`"
                              >
                                <div v-if="line.type === 'ratings'">
                                  <RevRating
                                    v-if="typeof value === 'number'"
                                    class="p-12"
                                    :score="value || 0"
                                    size="small"
                                  />
                                </div>
                                <div v-else-if="line.type === 'colors'">
                                  <ColorSwatches
                                    v-if="typeof value === 'object'"
                                    class="!justify-start p-12"
                                    :colors="value as Colors"
                                    size="medium"
                                  />
                                </div>
                                <ul
                                  v-else-if="line.type === 'tags'"
                                  class="flex list-none flex-wrap items-start gap-x-4 gap-y-8 p-12"
                                >
                                  <li
                                    v-for="(label, labelIndex) in value"
                                    :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_${labelIndex}`"
                                    class="flex"
                                  >
                                    <RevTag
                                      v-if="typeof label === 'string'"
                                      :label
                                      variant="secondary"
                                      variation="outline"
                                    />
                                  </li>
                                </ul>
                                <p
                                  v-else
                                  class="text-static-info-hi heading-2 p-12"
                                >
                                  {{ value }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </RevModalBase>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { ref, toRefs } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevModalBase, type TransitionAttrs } from '@ds/components/ModalBase'
import { RevRating } from '@ds/components/Rating'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag } from '@ds/components/Tag'
import { IconCompare } from '@ds/icons/IconCompare'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { useIntersectionObserver } from '@vueuse/core'

import { useProductComparisonModal } from '../../composables/useProductComparisonModal'
import type {
  ComparisonProduct,
  ProductSpecification,
} from '../../models/product'
import ColorSwatches from '../ColorSwatches.vue'
import ProductScore from '../ProductScore.vue'

import translations from './ProductComparison.translations'
import type { Colors } from './ProductComparison.types'
import ProductComparisonModalCard from './ProductComparisonModalCard.vue'

const i18n = useI18n()

const props = defineProps<{
  products: ComparisonProduct[]
  productsSpecifications: ProductSpecification[][]
  productsSpecificationsLoading: boolean
}>()

const { products, productsSpecifications } = toRefs(props)

const bodyContainer = ref()
const isScrolled = ref(false)
const { trackClick, trackComparisonView } = useTracking()
const route = useRoute()

const { tableData, blockImpressionTracked, sectionRef } =
  useProductComparisonModal({ products, productsSpecifications })

const transitionClassNames: TransitionAttrs = {
  enterActiveClass: tw`motion-safe:transition-transform motion-safe:duration-300`,
  enterFromClass: tw`translate-y-full`,
  leaveActiveClass: tw`motion-safe:transition-transform motion-safe:duration-200`,
  leaveToClass: tw`translate-y-full`,
}

useIntersectionObserver(
  bodyContainer,
  ([{ boundingClientRect }]) => {
    isScrolled.value = boundingClientRect.top < 0
  },
  {
    threshold: [0, 1],
  },
)

const gridClasses = 'grid grid-cols-2 gap-x-24 lg:grid-cols-4 lg:gap-x-40'

function handleOpen() {
  trackComparisonView({
    current_page: {
      page_type: 'comparison_result',
      referrer: {
        page_type: 'landing_page',
        params: {
          slugV2: route.params.slugV2,
          uuid: route.params.uuid,
        },
        products: products.value.map((product) => ({
          product_brand: product.brand,
          product_currency: product.price.currency,
          product_category: product.category,
          product_id: product.id,
          product_model: product.model,
          product_parent_title: product.title,
          product_price: product.price.amount,
        })),
      },
    },
  })
}

function handleClose(event?: { cause: string }): void {
  blockImpressionTracked.value = []
  if (event?.cause === 'dismiss')
    trackClick({
      name: 'close_comparison',
      zone: 'comparison_tool',
    })
}
</script>
